import localforage from 'localforage'
import { createLogger } from './logger'
import { PickUpKey } from './types'

const logger = createLogger({
  prefix: 'localForage'
})

export default class LocalForage {
  // eslint-disable-next-line no-use-before-define
  private static _instance: LocalForage

  public static instance(): LocalForage {
    if (!LocalForage._instance) {
      LocalForage._instance = new LocalForage()
    }
    return LocalForage._instance
  }

  private readonly _store: ({ type: 'LocalStorage' | 'IndexedDB' } & {
    // eslint-disable-next-line no-unused-vars
    [key in PickUpKey]: globalThis.LocalForage
  }) | null

  private constructor() {
    this._store = window.indexedDB
      ? {
          type: 'IndexedDB',
          entries: localforage.createInstance({
            name: 'pickUp',
            storeName: 'entries',
            driver: localforage.INDEXEDDB,
            description: 'This table stores all entries in IndexedDB'
          }),
          unsyncedChanges: localforage.createInstance({
            name: 'pickUp',
            storeName: 'unsyncedChanges',
            driver: localforage.INDEXEDDB,
            description: 'This table stores only unsynced changes in IndexedDB'
          }),
          kioskChanges: localforage.createInstance({
            name: 'pickUp',
            storeName: 'kioskChanges',
            driver: localforage.INDEXEDDB,
            description: 'This table stores unsynced kiosk changes in IndexedDB'
          })
        }
      : window.localStorage
        ? {
            type: 'LocalStorage',
            entries: localforage.createInstance({
              name: 'pickUp',
              driver: localforage.LOCALSTORAGE,
              description: 'This table stores all entries in localStorage'
            }),
            unsyncedChanges: localforage.createInstance({
              name: 'pickUp',
              driver: localforage.LOCALSTORAGE,
              description: 'This table stores only unsynced changes in localStorage'
            }),
            kioskChanges: localforage.createInstance({
              name: 'pickUp',
              driver: localforage.LOCALSTORAGE,
              description: 'This table stores unsynced kiosk changes in localStorage'
            })
          }
        : null
  }

  private _getKey(eventId: number, tableName: PickUpKey) {
    return this._store?.type === 'IndexedDB'
      ? `${eventId}`
      : `${tableName}/${eventId}`
  }

  async get<T>(
    tableName: PickUpKey,
    eventId: number
  ) {
    const storage = this._store

    if (!storage) {
      logger.error('Neither IndexedDB nor localStorage is supported in this browser.')
      return null
    }

    const key = this._getKey(eventId, tableName)

    try {
      return await storage[tableName].getItem<T[]>(key)
    } catch (err) {
      logger.error(`Error fetching data from ${storage.type}, Table: ${tableName}, EventId: ${eventId}`, err)
      return Promise.reject(err)
    }
  }

  async remove(
    tableName: PickUpKey,
    eventId: number
  ) {
    const storage = this._store

    if (!storage) {
      logger.error('Neither IndexedDB nor localStorage is supported in this browser.')
      return
    }

    const key = this._getKey(eventId, tableName)

    try {
      await storage[tableName].removeItem(key)
    }
    catch (err) {
      if (storage.type === 'IndexedDB') {
        logger.error(`Error removing data from ${storage.type}, Table: ${tableName}, EventId: ${eventId}`, err)
      }
      else if (storage.type === 'LocalStorage') {
        logger.error(`Error removing key ${key} from ${storage.type}`, err)
      }
    }
  }

  async set<T>(
    tableName: PickUpKey,
    eventId: number,
    data: T[],
  ) {
    const storage = this._store

    if (!storage) {
      logger.error('Neither IndexedDB nor localStorage is supported in this browser.')
      return false
    }

    const key = this._getKey(eventId, tableName)

    try {
      if (data.length) {
        await storage[tableName].setItem(key, data)
        return true
      }
      return false
    }
    catch (err) {
      logger.error(`Error storing data in ${storage.type}, Table: ${tableName}, EventId: ${eventId}:`, err)
      return Promise.reject(err)
    }
  }
}
